import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as moment from 'moment';
import { CalendarHeaderComponent } from 'src/app/shared/components/calendar-header/calendar-header.component';

class CustomDateAdapter extends MomentDateAdapter {
  getDayOfWeekNames(style: 'long' | 'short' | 'narrow') {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  }
}

@Component({
    selector: 'app-date-picker',
    templateUrl: './date-picker.component.html',
    styleUrls: ['../../../styles.scss', './date-picker.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: CustomDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: { dateInput: 'MM/DD/YYYY' },
                display: {
                    dateInput: 'MM/DD/YYYY',
                    monthYearLabel: 'MMMM YYYY',
                    dateA11yLabel: 'MM/DD/YYYY',
                    monthYearA11yLabel: 'MM YYYY',
                },
            },
        },
    ],
    standalone: false
})
export class DatePickerComponent implements OnInit, OnDestroy {
  // Shared Figma-styled calendar header (chevrons + white-chip tooltip).
  public calendarHeader = CalendarHeaderComponent;
  @ViewChild('input') input: ElementRef | undefined;

  @Input() label = '';
  /** A status pill after the label (e.g. "Autofilled", Figma Status badge 35281:21972); empty = none. */
  @Input() labelBadge = '';
  @Input() placeholder = '';
  @Input() parentFormGroup: FormGroup = new FormGroup({});
  @Input() controlName = '';
  @Input() error = '';
  @Input() focus = false;
  @Input() inputRestriction = '';
  @Input() min: any = moment('01/01/1900', 'L');
  @Input() max: any = moment('12/31/2200', 'L');
  @Output() checkForErrors = new EventEmitter();

  fieldSubscription: Subscription | undefined;

  isRequired = false;
  isDisabled = false;

  constructor() {
  }

  // Do not show field as touched and having error when clicked outside the field
  @HostListener('focusout')
  onBlur() {
    if (!this.error) {
      this.parentFormGroup.controls[this.controlName].markAsUntouched();
    }
  }

  ngOnInit(): void {
    const control = this.parentFormGroup.controls[this.controlName];
    this.isDisabled = control?.status === 'DISABLED';
    this.fieldSubscription = control?.valueChanges.subscribe((value) => {

      // Set max value
      if (value?._i?.length > 10 && value?.isAfter(this.max)) {
        this.parentFormGroup.controls[this.controlName].setValue(this.max);
      }

      // Set min value
      if (value?._i?.length > 10 && value?.isBefore(this.min)) {
        this.parentFormGroup.controls[this.controlName].setValue(this.min);
      }

      // If field is marked as touched, mark it as untouched
      if (this.parentFormGroup.controls[this.controlName]?.touched) {
        this.parentFormGroup.controls[this.controlName].markAsUntouched();
      }

      // Get errors on the form field
      const errors = this.parentFormGroup?.controls?.[this.controlName]?.errors;
      // If errors exist, handle it
      if (errors) {
        // Clear errors on the form field
        this.parentFormGroup.controls[this.controlName].setErrors(null);

        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);

        // Add errors again to the form field for showing messages in the future
        this.parentFormGroup.controls[this.controlName].setErrors(errors);
      } else {
        // Clear out the error messages on the screen (parent component)
        this.checkForErrors.emit(this.controlName);
      }
    });

    // Check if field is required
    if (control?.validator) {
      const validator = control?.validator({} as AbstractControl);
      this.isRequired = !!(validator && validator['required']);
    }
  }

  ngOnDestroy() {
    this.fieldSubscription?.unsubscribe();
  }
}
